import React, { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import Select from 'react-select'
import makeAnimated from 'react-select/animated';
import LoadingIcon from '@/icons/loading.svg'

interface FiltersProps {
  dataFilters: {
    device_types: string[]
    browsers: string[]
    csat_options?: string[]
    nps_options?: string[]
    contact_options: string[]
    disability_types: string[],
    csat_question_id?: number
  }
}

interface FiltersState {
  device: string[];
  browser: string[];
  dateFrom: string;
  dateTo: string;
  csat: string[];
  nps: string[];
  contact_type: string[];
  disability_type: string[];
}

function getInitialFilters(): FiltersState {
  const params = new URLSearchParams(window.location.search);
  const device = params.getAll('device_type[]');
  const browser = params.getAll('browser[]');
  const contact_type = params.getAll('contact_type[]');
  const disability_type = params.getAll('disability_type[]');
  const csat = params.getAll('csat[]');
  const nps = params.getAll('nps[]');
  return {
    device: device.length > 0 ? device : [],
    browser: browser.length > 0 ? browser : [],
    dateFrom: params.get('start_at') || '',
    dateTo: params.get('end_at') || '',
    csat: csat.length > 0 ? csat : [],
    nps: nps.length > 0 ? nps : [],
    contact_type: contact_type.length > 0 ? contact_type : [],
    disability_type: disability_type.length > 0 ? disability_type : [],
    // search: params.get('search') || ''
  };
}


const Filters: React.FC<FiltersProps> = ({ dataFilters }) => {
  const [filters, setFilters] = useState(getInitialFilters())
  const [lastFilters, setLastFilters] = useState(getInitialFilters())
  const [collapsed, setCollapsed] = useState(false)
  const [loading, setLoading] = useState(false)
  const animatedComponents = makeAnimated();

  const deviceOptions = [
    { value: 'Desktop', label: 'Desktop' },
    { value: 'Mobile', label: 'Mobile' },
    { value: 'Tablet', label: 'Tablet' }
  ]

  const browserOptions = [
    { value: 'Chrome', label: 'Chrome' },
    { value: 'Firefox', label: 'Firefox' },
    { value: 'Safari', label: 'Safari' },
    { value: 'Edge', label: 'Edge' }
  ]

  const csatOptions = (dataFilters.csat_options || []).map((opt: any) => {
    if (typeof opt === 'string') {
      return { value: opt, label: opt };
    } else {
      return { value: opt.value ?? opt.text ?? '', label: opt.text ?? opt.value ?? '' };
    }
  });

  const npsOptions = [
    { value: '9_10', label: 'Promotores (9 a 10)' },
    { value: '7_8', label: 'Pasivos (7 a 8)' },
    { value: '0_6', label: 'Detractor (0 a 6)' }
  ]

  const contactOptions = [
    { value: 'phone', label: 'Celular' },
    { value: 'email', label: 'Correo' }
  ]

  const disabilityOptions = [
    { value: 'visual', label: 'Discapacidad visual' },
    { value: 'hearing', label: 'Discapacidad auditiva' },
    { value: 'motor', label: 'Discapacidad motora' },
    { value: 'cognitive', label: 'Discapacidad cognitiva' }
  ]

  useEffect(() => {
    if (JSON.stringify(filters) !== JSON.stringify(lastFilters)) {
      setLoading(true)
      const params: Record<string, any> = {};

      if (filters.device && filters.device.length > 0) params.device_type = filters.device
      if (filters.browser && filters.browser.length > 0 ) params.browser = filters.browser
      if (filters.dateFrom) params.start_at = filters.dateFrom
      if (filters.dateTo) params.end_at = filters.dateTo
      if (filters.csat && filters.csat.length > 0) params.csat = filters.csat
      if (filters.nps && filters.nps.length > 0) params.nps = filters.nps
      if (filters.contact_type && filters.contact_type.length > 0) params.contact_type = filters.contact_type
      if (filters.disability_type && filters.disability_type.length > 0) params.disability_type = filters.disability_type
      if (dataFilters.csat_question_id) { params.csat_question_id = dataFilters.csat_question_id }

      router.get(window.location.pathname, params, {
        replace: true,
        preserveState: true,
        onFinish: () => setLoading(false)
      })
      setLastFilters(filters)
    }
  }, [filters, lastFilters])

  return (
    <div className="bg-gray-200 rounded-lg p-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Filtros de búsqueda</h2>
        <button
          className="text-blue-600 flex justify-between items-center hover:text-blue-800 text-sm"
          onClick={() => setCollapsed(!collapsed)}
        >
          Filtros de búsqueda
          <svg
            className={`w-6 h-6 transform transition-transform ml-4 ${!collapsed ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {!collapsed && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 my-4">
            {/* Dispositivo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dispositivo</label>
              <Select
                isMulti
                options={deviceOptions}
                isSearchable={false}
                closeMenuOnSelect={false}
                components={animatedComponents}
                value={deviceOptions.filter(opt => filters.device.includes(opt.value))}
                onChange={selected => setFilters({ ...filters, device: selected ? selected.map(opt => opt.value) : [] })}
                className="w-full"
                placeholder="Selecciona dispositivos..."
              />
            </div>

            {/* Navegador */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Navegador</label>
              <Select
                isMulti
                options={browserOptions}
                isSearchable={false}
                closeMenuOnSelect={false}
                value={browserOptions.filter(opt => filters.browser.includes(opt.value))}
                onChange={selected => setFilters({ ...filters, browser: selected ? selected.map(opt => opt.value) : [] })}
                className="w-full"
                placeholder="Selecciona navegadores..."
              />
            </div>

            {/* Fecha desde */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Desde</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={e => setFilters({ ...filters, dateFrom: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg"
              />
            </div>

            {/* Fecha hasta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Hasta</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={e => setFilters({ ...filters, dateTo: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Clasificación CSAT */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clasificación por Satisfacción (CSAT)
              </label>
              <Select
                options={csatOptions}
                isMulti
                isSearchable={false}
                closeMenuOnSelect={false}
                value={csatOptions.filter(opt => filters.csat.includes(opt.value))}
                onChange={selected => setFilters({ ...filters, csat: selected ? selected.map(opt => opt.value) : [] })}
                className="w-full"
                placeholder="Selecciona clasificación CSAT..."
              />
            </div>

            {/* Clasificación NPS */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clasificación por Promotores (NPS)
              </label>
              <Select
                options={npsOptions}
                isMulti
                isSearchable={false}
                closeMenuOnSelect={false}
                value={npsOptions.filter(opt => filters.nps.includes(opt.value))}
                onChange={selected => setFilters({ ...filters, nps: selected ? selected.map(opt => opt.value) : [] })}
                className="w-full"
                placeholder="Selecciona clasificación NPS..."
              />
            </div>

            {/* Medio de contacto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Medio de contacto</label>
              <Select
                isMulti
                options={contactOptions}
                isSearchable={false}
                closeMenuOnSelect={false}
                value={contactOptions.filter(opt => filters.contact_type.includes(opt.value))}
                onChange={selected => setFilters({ ...filters, contact_type: selected ? selected.map(opt => opt.value) : [] })}
                className="w-full"
                placeholder="Selecciona tipos de contacto..."
              />
            </div>

            {/* Tipo discapacidad */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo discapacidad</label>
              <Select
                isMulti
                options={disabilityOptions}
                isSearchable={false}
                closeMenuOnSelect={false}
                value={disabilityOptions.filter(opt => filters.disability_type.includes(opt.value))}
                onChange={selected => setFilters({ ...filters, disability_type: selected ? selected.map(opt => opt.value) : [] })}
                className="w-full"
                placeholder="Selecciona tipos de discapacidad..."
              />
            </div>
          </div>
          <div>
            {loading && (
              <div className="flex justify-start items-center py-4 mt-3">
                <img src={LoadingIcon} alt="Loading..." className="h-6 w-6 mr-2" />
                <span>Cargando...</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Filters;